import { useEffect, useState, useCallback } from "react";
import { Search, Sparkles, Loader2, ChevronDown, CheckCircle2, GitMerge, Trash2, Pencil, ListChecks, MessageSquareText, BookText, FolderOpen } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "../lib/api";
import { FASE_STYLE } from "../lib/constants";
import { ClusterCard } from "../components/ClusterCard";
import { KnowledgeEditDialog } from "../components/KnowledgeEditDialog";
import { ConfirmDialog } from "../components/ConfirmDialog";
import Contents from "./Contents";

const TABS = [
  { key: "script", label: "Script", icon: MessageSquareText },
  { key: "linea_guida", label: "Linee guida", icon: BookText },
  { key: "review", label: "Da revisionare", icon: ListChecks },
  { key: "contenuti", label: "Contenuti", icon: FolderOpen },
];

export default function KnowledgeBase() {
  const [tab, setTab] = useState("script");
  const [tree, setTree] = useState(null);
  const [review, setReview] = useState(null);
  const [intents, setIntents] = useState([]);
  const [reviewCount, setReviewCount] = useState(0);
  const [q, setQ] = useState("");
  const [semantic, setSemantic] = useState(false);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState({});
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const loadMeta = useCallback(async () => {
    try { const { data } = await api.get("/kb/meta"); setIntents(data.intents); } catch (e) { toast.error(errorMessage(e)); }
  }, []);
  useEffect(() => { loadMeta(); }, [loadMeta]);

  const loadTree = useCallback(async (tipo) => {
    setLoading(true);
    try {
      const { data } = await api.get("/kb/tree", { params: { tipo } });
      setTree(data); setReviewCount(data.review_count);
    } catch (e) { toast.error(errorMessage(e)); } finally { setLoading(false); }
  }, []);

  const loadReview = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/kb/review"); setReview(data); setReviewCount((data.intenti_da_confermare.length + data.clusters_incerti.length + data.clusters_troppo_varianti.length)); }
    catch (e) { toast.error(errorMessage(e)); } finally { setLoading(false); }
  }, []);

  const refresh = useCallback(() => {
    if (tab === "review") loadReview();
    else if (tab === "script" || tab === "linea_guida") loadTree(tab);
  }, [tab, loadReview, loadTree]);

  useEffect(() => { setResults(null); setQ(""); refresh(); }, [tab, refresh]);

  const runSearch = useCallback(async () => {
    if (!q.trim() || tab === "review" || tab === "contenuti") { setResults(null); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/kb/search", { params: { q, tipo: tab, mode: semantic ? "semantic" : "text", page_size: 100 } });
      setResults(data.items);
    } catch (e) { toast.error(errorMessage(e)); } finally { setLoading(false); }
  }, [q, tab, semantic]);

  useEffect(() => { const t = setTimeout(runSearch, semantic ? 500 : 300); return () => clearTimeout(t); }, [runSearch, semantic]);

  const onSaved = () => refresh();
  const doDelete = async () => {
    try { await api.delete(`/kb/cluster/${deleting.id}`); toast.success("Cluster eliminato"); refresh(); }
    catch (e) { toast.error(errorMessage(e)); } finally { setDeleting(null); }
  };
  const doSplit = async (c) => {
    try { const { data } = await api.post(`/kb/cluster/${c.id}/split`); toast.success(`${data.creati} varianti separate in cluster distinti`); refresh(); }
    catch (e) { toast.error(errorMessage(e)); }
  };
  const confirmIntent = async (id) => { try { await api.post(`/kb/intents/${id}/confirm`); toast.success("Intento confermato"); loadMeta(); loadReview(); } catch (e) { toast.error(errorMessage(e)); } };
  const renameIntent = async (it) => {
    const label = window.prompt("Nuovo nome intento:", it.label);
    if (!label || label === it.label) return;
    try { await api.put(`/kb/intents/${it.id}`, { label }); toast.success("Intento rinominato"); loadMeta(); loadReview(); } catch (e) { toast.error(errorMessage(e)); }
  };
  const mergeIntent = async (it, target) => {
    if (!target) return;
    try { await api.post(`/kb/intents/${it.id}/merge`, { target_intent_id: target }); toast.success("Intenti uniti"); loadMeta(); loadReview(); } catch (e) { toast.error(errorMessage(e)); }
  };
  const deleteIntent = async (it) => {
    if (!window.confirm(`Eliminare l'intento "${it.label}" e i suoi ${it.n_cluster} cluster?`)) return;
    try { await api.delete(`/kb/intents/${it.id}`); toast.success("Intento eliminato"); loadMeta(); loadReview(); } catch (e) { toast.error(errorMessage(e)); }
  };
  const clearIncerto = async (c) => { try { await api.put(`/kb/cluster/${c.id}`, { incerto: false }); toast.success("Segnato come rivisto"); loadReview(); } catch (e) { toast.error(errorMessage(e)); } };

  const intentStato = (id) => intents.find((i) => i.id === id)?.stato;

  return (
    <div data-testid="knowledge-page" className="space-y-6">
      <div className="fade-up">
        <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Base di conoscenza</p>
        <h1 className="mt-2 text-4xl font-bold tracking-tight sm:text-5xl">Knowledge Base</h1>
        <p className="mt-2 text-sm text-muted-foreground">Organizzata per fase → intento → cluster. Gli script sono suggeribili in live; le linee-guida sono solo coaching.</p>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="kb-tabs">
        {TABS.map((t) => {
          const Icon = t.icon; const active = tab === t.key;
          return (
            <button key={t.key} data-testid={`kb-tab-${t.key}`} onClick={() => setTab(t.key)} className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-all ${active ? "border-primary bg-primary/15 text-white" : "border-border text-muted-foreground hover:text-white"}`}>
              <Icon className="h-4 w-4" /> {t.label}
              {t.key === "review" && reviewCount > 0 && <span className="rounded-full bg-amber-500/20 px-1.5 text-xs text-amber-300">{reviewCount}</span>}
            </button>
          );
        })}
      </div>

      {tab !== "review" && tab !== "contenuti" && (
        <div className="surface flex flex-col gap-3 p-4 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input data-testid="knowledge-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={semantic ? "Descrivi cosa cerchi…" : "Cerca nei cluster…"} className="w-full rounded-full border border-input bg-background py-2.5 pl-11 pr-4 text-sm outline-none focus:border-primary" />
          </div>
          <button data-testid="semantic-toggle" aria-pressed={semantic} onClick={() => setSemantic(!semantic)} className={`inline-flex items-center gap-2 rounded-full border px-4 py-2.5 text-sm font-medium transition-all ${semantic ? "border-[#F5B942]/50 bg-[#F5B942]/15 text-[#FCD990]" : "border-border text-muted-foreground hover:text-white"}`}>
            <Sparkles className="h-4 w-4" /> Semantica AI
          </button>
        </div>
      )}

      {loading && !tree && !review && <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 spin-slow" /> Caricamento…</div>}

      {/* Search results */}
      {results && tab !== "review" && (
        <div data-testid="kb-search-results" className="space-y-3">
          <p className="text-xs text-muted-foreground">{results.length} risultati</p>
          <div className="grid gap-3 md:grid-cols-2">
            {results.map((c) => <ClusterCard key={c.id} cluster={c} onEdit={setEditing} onDelete={setDeleting} onSplit={doSplit} daConfermare={intentStato(c.intent_id) === "da_confermare"} />)}
          </div>
          {results.length === 0 && <div className="surface p-8 text-center text-sm text-muted-foreground">Nessun cluster trovato.</div>}
        </div>
      )}

      {/* Tree browse */}
      {!results && tab !== "review" && tab !== "contenuti" && tree && (
        <div className="space-y-3">
          {tree.fasi.every((f) => f.intenti.length === 0) && <div data-testid="knowledge-empty" className="surface p-12 text-center text-sm text-muted-foreground">Nessun cluster {tab === "script" ? "script" : "linea-guida"}. Carica contenuti per popolare questa sezione.</div>}
          {tree.fasi.filter((f) => f.intenti.length > 0).map((f) => {
            const st = FASE_STYLE[f.fase]; const isOpen = open[f.fase] ?? false;
            return (
              <section key={f.fase} data-testid={`fase-${f.fase}`} className="surface overflow-hidden">
                <button data-testid={`fase-toggle-${f.fase}`} onClick={() => setOpen({ ...open, [f.fase]: !isOpen })} className="flex w-full items-center gap-3 p-4 text-left">
                  <span className="h-8 w-1.5 rounded-full" style={{ background: st.bar }} />
                  <span className="flex-1">
                    <span className="text-base font-semibold md:text-lg">{f.label}</span>
                    <span className="ml-2 font-mono text-xs text-muted-foreground">{f.n_cluster} cluster</span>
                    <span className="block text-xs text-muted-foreground">{f.descrizione}</span>
                  </span>
                  <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </button>
                {isOpen && (
                  <div className="space-y-5 border-t border-border p-4">
                    {f.intenti.map((it) => (
                      <div key={it.id} data-testid={`intento-${it.id}`}>
                        <div className="mb-2 flex items-center gap-2">
                          <span className="text-sm font-semibold" style={{ color: st.text }}>{it.label}</span>
                          <span className="font-mono text-xs text-muted-foreground">({it.n_cluster})</span>
                          {it.stato === "da_confermare" && <span className="rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[11px] text-amber-300">da confermare</span>}
                        </div>
                        <div className="grid gap-3 md:grid-cols-2">
                          {it.clusters.map((c) => <ClusterCard key={c.id} cluster={c} onEdit={setEditing} onDelete={setDeleting} onSplit={doSplit} daConfermare={it.stato === "da_confermare"} />)}
                        </div>
                        {it.clusters.length === 0 && <p className="text-xs italic text-muted-foreground">Nessuna frase ancora — si popolerà quando carichi contenuti su questo tema.</p>}
                      </div>
                    ))}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}

      {/* Review queue */}
      {tab === "review" && review && (
        <div data-testid="review-queue" className="space-y-8">
          <section>
            <h2 className="text-base font-semibold md:text-lg">Intenti da confermare <span className="font-mono text-muted-foreground">({review.intenti_da_confermare.length})</span></h2>
            <p className="text-xs text-muted-foreground">Conferma, rinomina o accorpa gli intenti proposti automaticamente.</p>
            <div className="mt-3 space-y-2">
              {review.intenti_da_confermare.length === 0 && <p className="text-sm text-muted-foreground">Nessun intento in attesa.</p>}
              {review.intenti_da_confermare.map((it) => (
                <div key={it.id} data-testid={`review-intent-${it.id}`} className="surface flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <span className="text-sm font-semibold">{it.label}</span>
                    <span className="ml-2 text-xs text-muted-foreground">{it.fase_label} · {it.n_cluster} cluster</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button data-testid={`intent-confirm-${it.id}`} onClick={() => confirmIntent(it.id)} className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-3 py-1.5 text-xs text-emerald-300 hover:bg-emerald-500/20"><CheckCircle2 className="h-3.5 w-3.5" /> Conferma</button>
                    <button data-testid={`intent-rename-${it.id}`} onClick={() => renameIntent(it)} className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs text-muted-foreground hover:text-white"><Pencil className="h-3.5 w-3.5" /> Rinomina</button>
                    <select data-testid={`intent-merge-${it.id}`} defaultValue="" onChange={(e) => mergeIntent(it, e.target.value)} className="rounded-full border border-border bg-background px-3 py-1.5 text-xs text-muted-foreground outline-none">
                      <option value="" disabled>Accorpa a…</option>
                      {review.intenti_disponibili.filter((x) => x.id !== it.id && x.fase === it.fase).map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
                    </select>
                    <button data-testid={`intent-delete-${it.id}`} onClick={() => deleteIntent(it)} className="rounded-full border border-border p-1.5 text-muted-foreground hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h2 className="text-base font-semibold md:text-lg">Frasi non classificate con certezza <span className="font-mono text-muted-foreground">({review.clusters_incerti.length})</span></h2>
            <div className="mt-3 grid gap-3 md:grid-cols-2">
              {review.clusters_incerti.length === 0 && <p className="text-sm text-muted-foreground">Nessuna frase incerta.</p>}
              {review.clusters_incerti.map((c) => (
                <div key={c.id}>
                  <ClusterCard cluster={c} onEdit={setEditing} onDelete={setDeleting} />
                  <button data-testid={`incerto-ok-${c.id}`} onClick={() => clearIncerto(c)} className="mt-1 inline-flex items-center gap-1.5 text-xs text-emerald-300 hover:underline"><CheckCircle2 className="h-3.5 w-3.5" /> Classificazione corretta</button>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h2 className="text-base font-semibold md:text-lg">Cluster con troppe varianti <span className="font-mono text-muted-foreground">({review.clusters_troppo_varianti.length})</span></h2>
            <p className="text-xs text-muted-foreground">Valuta se separarli in cluster distinti.</p>
            <div className="mt-3 grid gap-3 md:grid-cols-2">
              {review.clusters_troppo_varianti.length === 0 && <p className="text-sm text-muted-foreground">Nessun cluster sovraffollato.</p>}
              {review.clusters_troppo_varianti.map((c) => (
                <div key={c.id}>
                  <ClusterCard cluster={c} onEdit={setEditing} onDelete={setDeleting} onSplit={doSplit} />
                  <button data-testid={`review-split-${c.id}`} onClick={() => doSplit(c)} className="mt-1 inline-flex items-center gap-1.5 text-xs text-amber-300 hover:underline"><GitMerge className="h-3.5 w-3.5 rotate-90" /> Separa le {c.n_varianti} varianti</button>
                </div>
              ))}
            </div>
          </section>
        </div>
      )}

      {tab === "contenuti" && <Contents embedded />}

      <KnowledgeEditDialog item={editing} intents={intents} onClose={() => setEditing(null)} onSaved={onSaved} />
      <ConfirmDialog open={!!deleting} title="Eliminare questo cluster?" description="Verrà rimosso dalla knowledge base e dall'indice vettoriale. L'operazione non è reversibile." onCancel={() => setDeleting(null)} onConfirm={doDelete} />
    </div>
  );
}
